import type { CloseEndedQuestion, Option } from '@ilteducation/exercise-service-types';

export type PlayedQuestion = {
  index: number;
  id: string;
  phrase: string;
  options: Option[];
  correctAnswerId: string;
};

/** questionId -> playerId -> chosen optionId */
export type AnswersByQuestion = Record<string, Record<string, string>>;

export type MatrixPlayer = {
  id: string;
  label: string;
  avatar?: string;
};

export type CellState = 'correct' | 'wrong' | 'none';

/** Cells sit in column order: `row.cells[i]` belongs to `columns[i]`. */
export type MatrixCell = {
  state: CellState;
  letter: string;
  chosenOptionText: string | null;
};

export type MatrixRow = {
  playerId: string;
  label: string;
  avatar?: string;
  cells: MatrixCell[];
  correct: number;
  answered: number;
  total: number;
};

export type MatrixColumn = {
  questionIndex: number;
  questionId: string;
  phrase: string;
  options: Option[];
  correctAnswerId: string;
  correctLetter: string;
  correctText: string;
  answered: number;
  correct: number;
  /** Share of the whole class, not of respondents: a skipped question is wrong. */
  pctCorrect: number;
};

export type Matrix = {
  rows: MatrixRow[];
  columns: MatrixColumn[];
  participantCount: number;
  questionCount: number;
};

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export const optionLetter = (index: number): string => LETTERS[index] ?? '?';

const letterForOption = (question: PlayedQuestion, optionId: string): string => {
  const index = question.options.findIndex((o) => o.id === optionId);
  return index === -1 ? '?' : optionLetter(index);
};

const textForOption = (question: PlayedQuestion, optionId: string): string | null =>
  question.options.find((o) => o.id === optionId)?.value ?? null;

export const buildAnswerRecapMatrix = (
  questions: PlayedQuestion[],
  answersByQuestion: AnswersByQuestion,
  players: MatrixPlayer[],
): Matrix => {
  const columns: MatrixColumn[] = questions.map((question) => {
    const answers = answersByQuestion[question.id] ?? {};

    let answered = 0;
    let correct = 0;
    players.forEach((player) => {
      const chosen = answers[player.id];
      if (chosen === undefined) {
        return;
      }
      answered += 1;
      if (chosen === question.correctAnswerId) {
        correct += 1;
      }
    });

    return {
      questionIndex: question.index,
      questionId: question.id,
      phrase: question.phrase,
      options: question.options,
      correctAnswerId: question.correctAnswerId,
      correctLetter: letterForOption(question, question.correctAnswerId),
      correctText: textForOption(question, question.correctAnswerId) ?? '',
      answered,
      correct,
      pctCorrect: players.length === 0 ? 0 : Math.round((correct / players.length) * 100),
    };
  });

  const rows: MatrixRow[] = players.map((player) => {
    let correct = 0;
    let answered = 0;

    const cells: MatrixCell[] = questions.map((question) => {
      const chosen = (answersByQuestion[question.id] ?? {})[player.id];

      if (chosen === undefined) {
        return { state: 'none', letter: '–', chosenOptionText: null };
      }

      answered += 1;
      const isCorrect = chosen === question.correctAnswerId;
      if (isCorrect) {
        correct += 1;
      }

      return {
        state: isCorrect ? 'correct' : 'wrong',
        letter: letterForOption(question, chosen),
        chosenOptionText: textForOption(question, chosen),
      };
    });

    return {
      playerId: player.id,
      label: player.label,
      avatar: player.avatar,
      cells,
      correct,
      answered,
      total: questions.length,
    };
  });

  return {
    rows,
    columns,
    participantCount: players.length,
    questionCount: questions.length,
  };
};

/** Most correct answers first. Ties keep join order, since sort is stable. */
export const sortMatrixRows = (rows: MatrixRow[]): MatrixRow[] => [...rows].sort((a, b) => b.correct - a.correct);

/** The fields of a CloseEndedQuestion the answer recap payload carries. */
type EncodedQuestion = Pick<CloseEndedQuestion, 'id' | 'phrase' | 'options' | 'answer'>;

/**
 * The backend sends each answer as an index into its question's options, against
 * one shared player order, which is what keeps the frame inside the 128 KB
 * websocket limit. Decode it into the optionIds the matrix works in.
 */
export const decodeAnswerRecapMatrix = (
  quiz: EncodedQuestion[],
  answerPlayerOrder: string[],
  encoded: Record<string, number[]>,
): { questions: PlayedQuestion[]; answersByQuestion: AnswersByQuestion } => {
  const questions: PlayedQuestion[] = [];
  const answersByQuestion: AnswersByQuestion = {};

  quiz.forEach((question, index) => {
    const questionId = question.id;
    if (!questionId) {
      return;
    }

    questions.push({
      index,
      id: questionId,
      phrase: question.phrase,
      options: question.options,
      correctAnswerId: question.answer,
    });

    const forQuestion: Record<string, string> = {};
    (encoded[questionId] ?? []).forEach((optionIndex, slot) => {
      const playerId = answerPlayerOrder[slot];
      // The backend's -1 for "did not answer" misses here, as does any index
      // outside the question, so both leave the player out of the row.
      const option = question.options[optionIndex];
      if (playerId && option) {
        forQuestion[playerId] = option.id;
      }
    });
    answersByQuestion[questionId] = forQuestion;
  });

  return { questions, answersByQuestion };
};
